import { normalizeUiLocale } from './locale';

const TOOL_CALL_TAGS = ['exam_order', 'long_exam_order', 'exam_pending', 'consultation_finish'];
const RECORDING_TAGS = ['subjective', 'objective', 'assessment', 'plan', 'diagnosis', 'treatment'];

const externalInputPattern = /^<external_input\b[^>]*>([\s\S]*?)<\/external_input>$/i;
const toolResponsePattern = /<tool_response\b[^>]*>([\s\S]*?)<\/tool_response>/gi;
const toolCallPattern = /<(exam_order|long_exam_order|exam_pending|consultation_finish)\b([^>]*)>([\s\S]*?)<\/\1>/gi;
const recordingOrEndPattern = /<(subjective|objective|assessment|plan|diagnosis|treatment|end)\b[^>]*>([\s\S]*?)<\/\1>/gi;

const normalizeWhitespace = (value) => (
  String(value || '')
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
);

const stringifyValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
};

const stableStringify = (value) => {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
};

const tryParseJson = (value) => {
  const text = String(value || '').trim();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch (_error) {
    return null;
  }
};

export const decodeInlineXmlEntities = (text) => {
  let decoded = String(text || '');

  for (let index = 0; index < 2; index += 1) {
    const next = decoded
      .replace(/&#x([0-9a-f]+);/gi, (_match, hex) => String.fromCharCode(parseInt(hex, 16)))
      .replace(/&#(\d+);/g, (_match, code) => String.fromCharCode(parseInt(code, 10)))
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/g, "'");

    if (next === decoded) {
      break;
    }
    decoded = next;
  }

  return decoded;
};

export const unwrapExternalInput = (content) => {
  const text = String(content || '').trim();
  const match = text.match(externalInputPattern);
  return match ? match[1] : text;
};

export const extractToolResponsePayloads = (content) => {
  const unwrapped = unwrapExternalInput(content);
  const payloads = [];
  let match;
  toolResponsePattern.lastIndex = 0;

  while ((match = toolResponsePattern.exec(unwrapped)) !== null) {
    payloads.push(match[1].trim());
  }

  return payloads;
};

export const cleanMessageContent = (content) => {
  if (!content) {
    return '';
  }

  return normalizeWhitespace(
    decodeInlineXmlEntities(content)
      .replace(/<tts\b[^>]*>([\s\S]*?)<\/tts>/gi, '$1')
      .replace(/<universal_medical\b[^>]*>[\s\S]*?<\/universal_medical>/gi, '')
  );
};

const cleanPlainTextFragment = (content) => (
  normalizeWhitespace(
    cleanMessageContent(content)
      .replace(/<[^>]+>/g, '')
  )
);

export const cleanVisibleMessageText = (content) => {
  const unwrapped = unwrapExternalInput(content);

  return cleanPlainTextFragment(
    unwrapped
      .replace(toolResponsePattern, '')
      .replace(/<(exam_order|long_exam_order|exam_pending|consultation_finish)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
      .replace(/<(subjective|objective|assessment|plan|diagnosis|treatment)\b[^>]*>([\s\S]*?)<\/\1>/gi, '$2')
      .replace(/<end\b[^>]*>[\s\S]*?<\/end>/gi, '')
  );
};

const normalizeToolResult = (value, rawPayload) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const normalizeDetailValue = (key, detailValue) => {
    if (detailValue === null || detailValue === undefined) {
      return null;
    }

    if (typeof detailValue === 'object' && !Array.isArray(detailValue)) {
      const detailKey = detailValue.key || detailValue.name || detailValue.code || detailValue.Code || key;
      const displayValue = detailValue.value ?? detailValue.result ?? detailValue.RESULT;
      if (displayValue === null || displayValue === undefined || String(displayValue).trim() === '') {
        return null;
      }

      return {
        key: stringifyValue(detailKey),
        value: stringifyValue(displayValue),
        label: stringifyValue(detailValue.label),
        label_local: stringifyValue(detailValue.label_local),
        label_zh: stringifyValue(detailValue.label_zh),
        name: stringifyValue(detailValue.name),
        name_en: stringifyValue(detailValue.name_en),
        name_local: stringifyValue(detailValue.name_local || detailValue.Name),
        unit: stringifyValue(detailValue.unit || detailValue.UNITS),
      };
    }

    if (String(detailValue).trim() === '') {
      return null;
    }

    return { key, value: stringifyValue(detailValue) };
  };

  const details = (() => {
    if (!value.details) {
      return [];
    }

    if (Array.isArray(value.details)) {
      return value.details
        .map((detailValue, index) => normalizeDetailValue(String(index), detailValue))
        .filter(Boolean);
    }

    if (typeof value.details === 'object') {
      return Object.entries(value.details)
        .map(([key, detailValue]) => normalizeDetailValue(key, detailValue))
        .filter(Boolean);
    }

    return [];
  })();

  return {
    type: stringifyValue(value.type),
    value: stringifyValue(value.value),
    unit: stringifyValue(value.unit),
    source: stringifyValue(value.source),
    executed_at: stringifyValue(value.executed_at),
    details,
    raw: value,
    rawPayload: rawPayload || JSON.stringify(value),
  };
};

export const parseToolResultContent = (content) => {
  const payloads = extractToolResponsePayloads(content);
  const candidates = payloads.length > 0 ? payloads : [unwrapExternalInput(content)];
  const results = [];
  const fallbackValues = [];

  candidates.forEach((candidate) => {
    const parsed = tryParseJson(candidate);
    const parsedValues = Array.isArray(parsed) ? parsed : [parsed];
    let parsedAnyResult = false;

    parsedValues.forEach((parsedValue) => {
      const result = normalizeToolResult(parsedValue, candidate);
      if (result) {
        parsedAnyResult = true;
        results.push(result);
      }
    });

    if (!parsedAnyResult) {
      const fallbackText = parsed
        ? JSON.stringify(parsed, null, 2)
        : cleanVisibleMessageText(candidate);
      if (fallbackText) {
        fallbackValues.push(fallbackText);
      }
    }
  });

  return {
    results,
    fallbackText: fallbackValues.join('\n\n'),
  };
};

const getToolPayloadSignatures = (content) => {
  const payloads = extractToolResponsePayloads(content);
  const candidates = payloads.length > 0 ? payloads : [unwrapExternalInput(content)];

  return candidates
    .map((candidate) => {
      const parsed = tryParseJson(candidate);
      return parsed ? stableStringify(parsed) : normalizeWhitespace(candidate);
    })
    .filter(Boolean);
};

const hasDuplicateToolMessage = (messages, messageIndex, message, payloads) => {
  const payloadSignatures = new Set(
    payloads
      .map((payload) => {
        const parsed = tryParseJson(payload);
        return parsed ? stableStringify(parsed) : normalizeWhitespace(payload);
      })
      .filter(Boolean)
  );

  if (payloadSignatures.size === 0) {
    return false;
  }

  return messages.some((candidate, candidateIndex) => {
    if (candidateIndex === messageIndex || String(candidate?.role || '').toLowerCase() !== 'tool') {
      return false;
    }

    const sameTurn = message?.turn !== undefined && candidate?.turn !== undefined && String(message.turn) === String(candidate.turn);
    const adjacent = Math.abs(candidateIndex - messageIndex) <= 1;
    if (!sameTurn && !adjacent) {
      return false;
    }

    return getToolPayloadSignatures(candidate.content).some((signature) => payloadSignatures.has(signature));
  });
};

const DEFAULT_CONVERSATION_LANGUAGE = 'en-US';
const chineseTextPattern = /[\u3400-\u9fff\uf900-\ufaff]/;
const arabicTextPattern = /[\u0600-\u06ff]/;
const thaiTextPattern = /[\u0e00-\u0e7f]/;
const vietnameseTextPattern = /[\u0102\u0103\u0110\u0111\u0128\u0129\u0168\u0169\u01a0\u01a1\u01af\u01b0\u1ea0-\u1ef9]/i;
const malayTextPattern = /\b(?:kesihatan|keputusan|rawatan|sedia|sila|klinik|ubat)\b/i;
const indonesianTextPattern = /\b(?:kesehatan|hasilnya|pengobatan|silakan|dokter|klinik|obat)\b/i;
const russianTextPattern = /[А-Яа-яЁё]/u;

const WELCOME_GREETINGS = {
  'en-US': {
    follow_up: "Hi again, I'm your AI Health Assistant. I can see you have a follow-up plan ready, so let's focus on how things have changed since your last visit and get today's review moving. How have you been since then?",
    checkup: 'Hi, I\'m your AI Health Assistant. Your routine Health House checkup package is ready, and the results will be sent to the doctor for review. Please say "I\'m ready" when you want to start the first measurement.',
    initial: "Hi, I'm your AI Health Assistant. Before you see your doctor, I'll take some notes about how you're feeling, and make sure your doctor has everything ready. How have you been?",
  },
  'ar-SA': {
    follow_up: 'مرحباً مجدداً، أنا مساعد الصحة الذكي. أرى أن لديك خطة مراجعة جاهزة، لذلك سنركّز اليوم على ما تغيّر منذ زيارتك السابقة وعلى الفحوصات المطلوبة الآن. كيف كنت منذ ذلك الوقت؟',
    checkup: 'مرحباً، أنا مساعد الصحة الذكي. سننجز حزمة الفحص الروتيني عبر Health House خطوة بخطوة ثم نرسل النتائج للطبيب لمراجعتها. هل أنت مستعد لبدء القياس الأول؟ إذا كنت مستعداً، قل: «أنا مستعد».',
    initial: 'مرحباً، أنا مساعد الصحة الذكي. قبل زيارة طبيبك، سأدوّن بعض الملاحظات عن شعورك حتى يكون كل شيء جاهزاً. كيف كنت تشعر مؤخراً؟',
  },
  'zh-CN': {
    follow_up: '您好，又见面了。我看到您已经有复诊计划，我们今天重点了解上次就诊后的变化，并完成本次复查。您最近感觉怎么样？',
    checkup: '您好，我是您的智能问诊助手。您的健康小屋常规体检项目已经准备好，结果会交给医生查看。想开始第一项测量时，请说“准备好了”。',
    initial: '您好，我是您的智能问诊助手。看医生前，我会先了解并记录您的情况，方便医生接着查看。您最近感觉怎么样？',
  },
  'zh-HK': {
    follow_up: '你好，又見面喇。我見到你已經有覆診安排，今日會重點了解你上次求診之後有咩變化，同埋完成今次要覆查嘅項目。你最近覺得點呀？',
    checkup: '你好，我係你嘅智能問診助手。你嘅健康小屋例行身體檢查項目已經準備好，結果會交畀醫生查看。想開始第一項量度嗰陣，請講「準備好」。',
    initial: '你好，我係你嘅智能問診助手。見醫生之前，我會先了解同記低你而家嘅情況，等醫生之後可以接住睇。你最近覺得點呀？',
  },
  'zh-TW': {
    follow_up: '您好，又見面了。我看到您已經有回診安排，今天會著重了解上次看診後的變化，並完成這次需要複查的項目。您最近感覺怎麼樣？',
    checkup: '您好，我是您的智慧問診助手。您的健康小屋例行健檢項目已經準備好，結果會提供給醫師查看。想開始第一項測量時，請說「準備好了」。',
    initial: '您好，我是您的智慧問診助手。看診前，我會先了解並記錄您的狀況，讓醫師接著查看。您最近感覺怎麼樣？',
  },
  'zh-MO': {
    follow_up: '你好，又見面喇。我見到你已經有覆診安排，今日會重點了解你上次就診之後有咩變化，同埋完成今次要覆查嘅項目。你最近覺得點呀？',
    checkup: '你好，我係你嘅智能問診助手。你嘅健康小屋例行身體檢查項目已經準備好，結果會交畀醫生查看。想開始第一項量度嗰陣，請講「準備好」。',
    initial: '你好，我係你嘅智能問診助手。見醫生之前，我會先了解同記錄你而家嘅情況，等醫生之後可以接住睇。你最近覺得點呀？',
  },
  'vi-VN': {
    follow_up: 'Chào bạn, tôi là Trợ lý Y tế AI của bạn. Tôi thấy bạn đã có kế hoạch tái khám, vì vậy hãy cùng tập trung vào những thay đổi kể từ lần thăm khám trước và bắt đầu buổi đánh giá hôm nay. Sức khỏe của bạn thế nào kể từ đó?',
    checkup: 'Chào bạn, tôi là Trợ lý Y tế AI của bạn. Gói khám sức khỏe định kỳ Health House của bạn đã sẵn sàng, kết quả sẽ được gửi đến bác sĩ để xem xét. Vui lòng nói "Tôi sẵn sàng" khi bạn muốn bắt đầu đo lường đầu tiên.',
    initial: 'Chào bạn, tôi là Trợ lý Y tế AI của bạn. Trước khi gặp bác sĩ, tôi sẽ ghi lại một số thông tin về tình trạng sức khỏe hiện tại của bạn để đảm bảo bác sĩ có đầy đủ thông tin cần thiết. Dạo này bạn cảm thấy thế nào?',
  },
  'th-TH': {
    follow_up: 'สวัสดีอีกครั้ง ยินดีต้อนรับสู่ระบบผู้ช่วยด้านสุขภาพ AI พบว่าแผนการติดตามผลพร้อมแล้ว จึงขอเน้นสอบถามความเปลี่ยนแปลงตั้งแต่ครั้งก่อน เพื่อเริ่มกระบวนการตรวจวันนี้ อาการเป็นอย่างไรบ้างหลังจากนั้น',
    checkup: 'สวัสดี ยินดีต้อนรับสู่ระบบผู้ช่วยด้านสุขภาพ AI แพ็คเกจตรวจสุขภาพ Health House แบบประจำพร้อมแล้ว โดยผลจะส่งให้แพทย์ตรวจสอบ กรุณาพูดว่า "ฉันพร้อม" เมื่อต้องการเริ่มการวัดค่าครั้งแรก',
    initial: 'สวัสดี ยินดีต้อนรับสู่ระบบผู้ช่วยด้านสุขภาพ AI ก่อนเข้าพบแพทย์ จะทำการบันทึกข้อมูลอาการเบื้องต้นเพื่อให้แพทย์มีข้อมูลครบถ้วน ช่วงนี้เป็นอย่างไรบ้าง',
  },
  'id-ID': {
    follow_up: 'Halo kembali, saya Asisten Kesehatan AI Anda. Saya melihat Anda sudah memiliki rencana tindak lanjut, jadi mari kita fokus pada perubahan yang terjadi sejak kunjungan terakhir dan memulai tinjauan hari ini. Bagaimana kondisi Anda sejak saat itu?',
    checkup: 'Halo, saya Asisten Kesehatan AI Anda. Paket pemeriksaan rutin Health House Anda sudah siap, dan hasilnya akan dikirimkan ke dokter untuk ditinjau. Silakan katakan "Saya siap" ketika Anda ingin memulai pengukuran pertama.',
    initial: 'Halo, saya Asisten Kesehatan AI Anda. Sebelum Anda bertemu dokter, saya akan mencatat beberapa hal tentang kondisi Anda saat ini untuk memastikan dokter memiliki semua informasi yang diperlukan. Bagaimana kondisi Anda?',
  },
  'ms-MY': {
    follow_up: 'Selamat kembali. Saya adalah Pembantu Kesihatan AI anda. Saya melihat anda mempunyai pelan susulan yang sedia ada, jadi mari kita tumpukan pada perubahan sejak lawatan terakhir anda dan mulakan semakan hari ini. Bagaimana keadaan anda sejak itu?',
    checkup: 'Hai, saya adalah Pembantu Kesihatan AI anda. Pakej pemeriksaan rutin Health House anda telah bersedia, dan keputusannya akan dihantar kepada doktor untuk semakan. Sila nyatakan "Saya bersedia" apabila anda ingin memulakan pengukuran pertama.',
    initial: 'Hai, saya adalah Pembantu Kesihatan AI anda. Sebelum anda berjumpa doktor, saya akan mencatat beberapa nota tentang perasaan anda dan memastikan doktor anda mempunyai semua maklumat yang diperlukan. Bagaimana keadaan anda?',
  },
  'ru-RU': {
    follow_up: 'Здравствуйте! Я ваш ИИ-помощник по здоровью. Я вижу, что для вас подготовлен план повторного визита. Давайте обсудим изменения после прошлого приёма. Как вы себя чувствуете с тех пор?',
    checkup: 'Здравствуйте! Я ваш ИИ-помощник по здоровью. Пакет планового обследования Health House готов, а результаты будут переданы врачу. Когда будете готовы начать первое измерение, скажите: «Я готов» или «Я готова».',
    initial: 'Здравствуйте! Я ваш ИИ-помощник по здоровью. До встречи с врачом я задам несколько вопросов о вашем самочувствии, чтобы подготовить необходимые сведения. Как вы себя чувствуете?',
  },
};

export const normalizeConversationLanguage = (language) => {
  const normalized = String(language || '').trim().toLowerCase().replace(/_/g, '-');
  if (!normalized) {
    return '';
  }
  if (
    normalized.startsWith('zh') ||
    normalized === 'cn' ||
    normalized.includes('chinese') ||
    normalized.includes('mandarin') ||
    normalized.includes('cantonese') ||
    normalized.includes('hong kong') ||
    normalized.includes('taiwan') ||
    normalized.includes('macau') ||
    normalized.includes('macao') ||
    normalized.includes('中文') ||
    normalized.includes('汉语') ||
    normalized.includes('廣東話') ||
    normalized.includes('粤语')
  ) {
    if (normalized.includes('hong kong') || normalized.includes('cantonese') || normalized.includes('廣東話') || normalized.includes('粤语')) {
      return 'zh-HK';
    }
    if (normalized.includes('macau') || normalized.includes('macao') || normalized.includes('澳門') || normalized.includes('澳门')) {
      return 'zh-MO';
    }
    if (normalized.includes('taiwan') || normalized.includes('台灣') || normalized.includes('台湾')) {
      return 'zh-TW';
    }
    return normalizeUiLocale(normalized, 'zh-CN');
  }
  if (normalized.startsWith('en') || normalized.includes('english')) {
    return 'en-US';
  }
  if (normalized.startsWith('ar') || normalized.includes('arabic') || normalized.includes('العربية')) {
    return 'ar-SA';
  }
  if (normalized.startsWith('vi') || normalized.includes('vietnamese') || normalized.includes('tiếng việt')) {
    return 'vi-VN';
  }
  if (normalized.startsWith('th') || normalized.includes('thai') || normalized.includes('ไทย')) {
    return 'th-TH';
  }
  if (normalized.startsWith('id') || normalized.includes('indonesian') || normalized.includes('bahasa indonesia')) {
    return 'id-ID';
  }
  if (normalized.startsWith('ms') || normalized.includes('malay') || normalized.includes('bahasa melayu')) {
    return 'ms-MY';
  }
  if (
    normalized.startsWith('ru')
    || normalized.includes('russian')
    || normalized.includes('русский')
    || normalized.includes('русском')
  ) {
    return 'ru-RU';
  }
  return '';
};

const parseObjectValue = (value) => {
  if (!value) {
    return null;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const parsed = tryParseJson(value);
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
};

const getLanguageFromObject = (value) => {
  const object = parseObjectValue(value);
  if (!object) {
    return '';
  }

  const candidates = [
    object.language,
    object.lang,
    object.locale,
    object.conversation_language,
    object.patient_language,
    object.preferred_language,
  ];

  for (const candidate of candidates) {
    const language = normalizeConversationLanguage(candidate);
    if (language) {
      return language;
    }
  }

  return '';
};

const getMessageLanguage = (message) => {
  if (!message || typeof message !== 'object') {
    return '';
  }

  const directLanguage = [
    message.language,
    message.lang,
    message.locale,
    message.conversation_language,
    message.patient_language,
    message.preferred_language,
  ].map(normalizeConversationLanguage).find(Boolean);

  if (directLanguage) {
    return directLanguage;
  }

  return [
    message.message_data,
    message.messageData,
    message.metadata,
  ].map(getLanguageFromObject).find(Boolean) || '';
};

export const inferConversationLanguage = (messages = [], explicitLanguage) => {
  const explicit = normalizeConversationLanguage(explicitLanguage);
  if (explicit) {
    return explicit;
  }

  const safeMessages = Array.isArray(messages) ? messages : [];
  const messageLanguage = safeMessages.map(getMessageLanguage).find(Boolean);
  if (messageLanguage) {
    return messageLanguage;
  }

  const visibleText = safeMessages
    .map((message) => cleanVisibleMessageText(message?.content || message?.raw_content || ''))
    .filter(Boolean)
    .join('\n');

  if (chineseTextPattern.test(visibleText)) return 'zh-CN';
  if (arabicTextPattern.test(visibleText)) return 'ar-SA';
  if (thaiTextPattern.test(visibleText)) return 'th-TH';
  if (vietnameseTextPattern.test(visibleText)) return 'vi-VN';
  if (malayTextPattern.test(visibleText)) return 'ms-MY';
  if (indonesianTextPattern.test(visibleText)) return 'id-ID';
  if (russianTextPattern.test(visibleText)) return 'ru-RU';
  return DEFAULT_CONVERSATION_LANGUAGE;
};

export const getWelcomeGreeting = (visitType, language = DEFAULT_CONVERSATION_LANGUAGE) => {
  const conversationLanguage = normalizeConversationLanguage(language) || DEFAULT_CONVERSATION_LANGUAGE;
  const greetings = WELCOME_GREETINGS[conversationLanguage] || WELCOME_GREETINGS[DEFAULT_CONVERSATION_LANGUAGE];
  return greetings[visitType] || greetings.initial;
};

const getWelcomeGreetingVariants = (visitType) => (
  Object.values(WELCOME_GREETINGS).map((greetings) => greetings[visitType] || greetings.initial)
);

const isKnownWelcomeGreeting = (content, visitType) => {
  const visibleText = cleanVisibleMessageText(content);
  return getWelcomeGreetingVariants(visitType).some((greeting) => visibleText === greeting);
};

const getMessageDate = (message) => {
  if (!message) {
    return null;
  }
  if (message.created_at) {
    const createdAt = new Date(message.created_at);
    if (!Number.isNaN(createdAt.getTime())) {
      return createdAt;
    }
  }
  if (message.timestamp !== undefined && message.timestamp !== null) {
    const timestamp = Number(message.timestamp);
    if (!Number.isNaN(timestamp)) {
      return new Date(timestamp * 1000);
    }
  }
  return null;
};

const getSyntheticWelcomeTime = (messages) => {
  const reference = messages.find((message) => message.role === 'user' && !message.synthetic) || messages.find((message) => !message.synthetic);
  const referenceDate = getMessageDate(reference);
  if (!referenceDate) {
    return new Date(Date.now() - 10000).toISOString();
  }
  return new Date(referenceDate.getTime() - 10000).toISOString();
};

export const normalizeReportConversationMessages = (messages = [], options = {}) => {
  const safeMessages = Array.isArray(messages) ? messages : [];
  const normalized = [];

  safeMessages.forEach((message, index) => {
    if (!message) {
      return;
    }

    const role = String(message.role || '').toLowerCase().trim();
    const content = String(message.content || '');

    if (role === 'user') {
      const toolPayloads = extractToolResponsePayloads(content);
      if (toolPayloads.length > 0) {
        if (!hasDuplicateToolMessage(safeMessages, index, message, toolPayloads)) {
          const toolResult = parseToolResultContent(content);
          normalized.push({
            ...message,
            id: message.id || `internal-tool-${index}`,
            role: 'system',
            displayKind: 'tool_result',
            content,
            toolResults: toolResult.results,
            fallbackText: toolResult.fallbackText,
          });
        }
        return;
      }

      const visibleText = cleanVisibleMessageText(content);
      if (visibleText) {
        normalized.push({
          ...message,
          role: 'user',
          content: visibleText,
        });
      }
      return;
    }

    if (role === 'tool') {
      const toolResult = parseToolResultContent(content);
      normalized.push({
        ...message,
        role: 'system',
        displayKind: 'tool_result',
        toolResults: toolResult.results,
        fallbackText: toolResult.fallbackText,
      });
      return;
    }

    if (role === 'assistant') {
      const cleanedContent = cleanMessageContent(content);
      if (cleanVisibleMessageText(cleanedContent) || hasToolCallXML(cleanedContent)) {
        normalized.push({
          ...message,
          role: 'assistant',
          content: cleanedContent,
        });
      }
      return;
    }

    const visibleText = cleanVisibleMessageText(content);
    if (visibleText) {
      normalized.push({
        ...message,
        role: 'system',
        content: visibleText,
      });
    }
  });

  if (normalized.length > 0) {
    const visitType = options.visitType || 'initial';
    const conversationLanguage = inferConversationLanguage(safeMessages, options.conversationLanguage);
    const welcomeGreeting = getWelcomeGreeting(visitType, conversationLanguage);
    const welcomeIndex = normalized.findIndex((message) => (
      message.role === 'assistant' && isKnownWelcomeGreeting(message.content, visitType)
    ));

    if (welcomeIndex >= 0) {
      if (cleanVisibleMessageText(normalized[welcomeIndex].content) !== welcomeGreeting) {
        normalized[welcomeIndex] = {
          ...normalized[welcomeIndex],
          content: welcomeGreeting,
        };
      }
    } else {
      normalized.unshift({
        id: 'synthetic-welcome-greeting',
        role: 'assistant',
        content: welcomeGreeting,
        created_at: getSyntheticWelcomeTime(normalized),
        synthetic: true,
      });
    }
  }

  return normalized;
};

export const formatConversationTime = (dateString, language = DEFAULT_CONVERSATION_LANGUAGE) => {
  const date = new Date(dateString);
  if (!dateString || Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(normalizeUiLocale(language, DEFAULT_CONVERSATION_LANGUAGE), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
};

export const hasToolCallXML = (content) => {
  if (!content) {
    return false;
  }
  return TOOL_CALL_TAGS.some((tagName) => new RegExp(`<${tagName}\\b[^>]*>`, 'i').test(content));
};

const parseAttributes = (attributesText = '') => {
  const attributes = {};
  const attributePattern = /([a-zA-Z_][\w-]*)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
  let match;

  while ((match = attributePattern.exec(attributesText)) !== null) {
    attributes[match[1]] = match[2] || match[3] || '';
  }

  return attributes;
};

const splitTextAndRecordingParts = (content) => {
  const parts = [];
  let cursor = 0;
  let match;

  recordingOrEndPattern.lastIndex = 0;
  while ((match = recordingOrEndPattern.exec(content)) !== null) {
    const before = cleanPlainTextFragment(content.slice(cursor, match.index));
    if (before) {
      parts.push({ type: 'text', text: before });
    }

    const tagName = match[1].toLowerCase();
    const text = cleanPlainTextFragment(match[2]);
    if (tagName !== 'end' && text) {
      parts.push({ type: 'recording', tagName, text });
    }

    cursor = match.index + match[0].length;
  }

  const after = cleanPlainTextFragment(content.slice(cursor));
  if (after) {
    parts.push({ type: 'text', text: after });
  }

  return parts;
};

export const splitAssistantMessageParts = (content) => {
  const cleanedContent = cleanMessageContent(content);
  const parts = [];
  let cursor = 0;
  let match;

  toolCallPattern.lastIndex = 0;
  while ((match = toolCallPattern.exec(cleanedContent)) !== null) {
    parts.push(...splitTextAndRecordingParts(cleanedContent.slice(cursor, match.index)));
    parts.push({
      type: 'tool_call',
      tagName: match[1].toLowerCase(),
      attributes: parseAttributes(match[2]),
      text: cleanPlainTextFragment(match[3]),
    });
    cursor = match.index + match[0].length;
  }

  parts.push(...splitTextAndRecordingParts(cleanedContent.slice(cursor)));

  return parts;
};

export const humanizeIdentifier = (value) => {
  const text = String(value || '').trim();
  if (!text) {
    return '';
  }

  return text
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const isRecordingTag = (tagName) => RECORDING_TAGS.includes(String(tagName || '').toLowerCase());
